<script setup lang="ts">
import { useIntervalFn } from "@vueuse/core";
import { twMerge } from "tailwind-merge";
import { computed, onMounted, ref } from "vue";

import { useDiscount } from "@/composables/api/useDiscount";
import { useUser } from "@/composables/api/useUser";
import { useI18n } from "@/i18n";
import { rewards } from "@/rewards";

import PiggyBankIcon from "@/components/icons/PiggyBankIcon.vue";
import TicketIcon from "@/components/icons/TicketIcon.vue";
import Spinner from "@/components/ui/Spinner.vue";

const { data: user } = useUser();
const { data: discount } = useDiscount();
const { t } = useI18n();

const currentImageIndex = ref(0);
const completedRotation = ref(false);

const totalRewards = computed(() => {
  if (!user.value?.current_mechanic) {
    return 0;
  }
  const mechanic = user.value.current_mechanic;
  if (mechanic === "lactel") {
    return rewards[mechanic].length - 1;
  }
  return rewards[mechanic].length;
});

const currentReward = computed(() => {
  if (!user.value?.current_mechanic || currentImageIndex.value >= totalRewards.value) {
    return {
      name: "",
      i18nName: "",
      images: { card: "" },
    };
  }
  const mechanic = user.value.current_mechanic;
  return rewards[mechanic][currentImageIndex.value];
});

const { pause, resume } = useIntervalFn(
  () => {
    currentImageIndex.value = (currentImageIndex.value + 1) % totalRewards.value;

    // If we've gone through all images once, stop the rotation and show first image
    if (currentImageIndex.value === 0 && !completedRotation.value) {
      completedRotation.value = true;
      pause();
      currentImageIndex.value = 0;
    }
  },
  2000,
  { immediate: false },
);

onMounted(() => {
  resume();
});
</script>

<template>
  <div
    :class="
      twMerge(
        'bg-galbani relative flex h-56 w-full flex-col justify-between overflow-hidden rounded-[10px] px-4 py-6 shadow-lg lg:h-96 lg:rounded-[40px] lg:p-10',
        user?.current_mechanic === 'lactel' && 'bg-lactel-yellow',
      )
    "
  >
    <img
      v-if="user?.current_mechanic === 'lactel'"
      class="absolute top-0 right-0 w-[149px] lg:w-[240px]"
      alt=""
      src="/lactel/images/lactel-club.png"
    />

    <h2
      :class="
        twMerge(
          'text-topchef font-brygada text-[28px] font-bold lg:text-[48px]',
          user?.current_mechanic === 'lactel' &&
            'text-lactel-blue font-mitr max-w-[55%] break-all lg:max-w-[68%]',
        )
      "
      v-html="user ? t('home.card.welcome', { firstname: user.first_name }) : 'Bonjour !'"
    />

    <div class="flex flex-col gap-1.5">
      <template v-if="user && discount">
        <div class="flex items-center gap-2">
          <PiggyBankIcon
            :class="
              twMerge(
                'size-8 text-white lg:size-12',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
          />

          <p
            :class="
              twMerge(
                'flex items-end gap-1 text-lg text-white lg:text-2xl',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue font-semibold',
              )
            "
          >
            <span
              :class="
                twMerge(
                  'font-brygada text-topchef text-[42px] leading-none font-bold lg:text-[52px]',
                  user?.current_mechanic === 'lactel' && 'text-lactel-blue font-mitr font-semibold',
                )
              "
            >
              {{ user.loyalty_points }}
            </span>
            point{{ user.loyalty_points === 1 || user.loyalty_points === 0 ? "" : "s" }}
          </p>
        </div>

        <div class="flex items-center gap-2">
          <TicketIcon
            :class="
              twMerge(
                'size-8 text-white lg:size-12',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
          />

          <p
            :class="
              twMerge(
                'flex items-end gap-1 text-lg text-white lg:text-2xl',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue font-semibold',
              )
            "
          >
            <span
              :class="
                twMerge(
                  'text-topchef font-brygada text-[42px] leading-none font-bold lg:text-[52px]',
                  user?.current_mechanic === 'lactel' && 'text-lactel-blue font-mitr font-semibold',
                )
              "
            >
              {{ discount.count }}
            </span>
            bon{{ discount.count === 1 || discount.count === 0 ? "" : "s" }}
          </p>
        </div>
      </template>

      <template v-else>
        <Spinner
          class="size-16 lg:size-24"
          trackColor="rgba(255, 255, 255, 0.3)"
          color="rgba(255, 255, 255, 1)"
        />
      </template>
    </div>

    <span
      v-if="user?.current_mechanic === 'galbani'"
      class="pointer-events-none absolute right-[-20%] bottom-[-30%] size-52 rounded-full bg-white lg:right-[-15%] lg:bottom-[-25%] lg:size-96"
    />

    <Transition name="fade" mode="out-in">
      <img
        v-if="currentReward.images.card"
        :key="currentImageIndex"
        :src="currentReward.images.card"
        :alt="currentReward.name"
        :class="
          twMerge(
            'pointer-events-none absolute right-[-20%] bottom-[-30%] size-52 rounded-full lg:right-[-15%] lg:bottom-[-25%] lg:size-96',
            user?.current_mechanic === 'lactel' &&
              'right-[-13%] bottom-[-35%] rounded-none object-contain lg:right-[-7%] lg:bottom-[-28%] lg:size-78',
            user?.current_mechanic === 'lactel' &&
              currentReward.i18nName === 'socks' &&
              'right-[-5%] bottom-[-5%] lg:right-[0%] lg:bottom-[-8%]',
          )
        "
      />
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
